import React, { createContext, useContext, ReactNode, useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { ExerciseDefinition } from '../utils/exercise'
import { Pose } from '../utils/pose'

interface ExerciseContextType {
  // Exercise-related props
  availableExercises: string[]
  selectedExerciseName: string | null
  shortExerciseName: string
  isLoadingExercises: boolean
  // Exercise actions
  setSelectedExerciseName: (exerciseName: string) => void
  // Additional exercise data
  getCurrentExercise: () => ExerciseDefinition | null
}

const ExerciseContext = createContext<ExerciseContextType | undefined>(undefined)

interface ExerciseProviderProps {
  children: ReactNode
}

export const ExerciseProvider: React.FC<ExerciseProviderProps> = ({
  children,
}) => {
  // Exercise database state
  const [availableExercises, setAvailableExercises] = useState<string[]>([])
  const [exerciseData, setExerciseData] = useState<{ [key: string]: ExerciseDefinition }>({})
  const [selectedExerciseName, setSelectedExerciseName] = useState<string | null>(null)

  const shortExerciseName = useMemo(() => {
    if (!selectedExerciseName) {
      return 'N/A'
    }

    const parts = selectedExerciseName.split('_')

    return parts?.[0]
  }, [selectedExerciseName])

  const [isLoadingExercises, setIsLoadingExercises] = useState<boolean>(false)

  const isInitialLoadRef = useRef<boolean>(true)

  const loadExercises = async () => {
    setIsLoadingExercises(true)

    try {
      // Fetch real exercise data from the public directory
      const response = await fetch('/exercises_v3.1.json')
      if (!response.ok) {
        throw new Error(`Failed to fetch exercises: ${response.status} ${response.statusText}`)
      }

      const exercisesData = await response.json()

      const exercises: { [key: string]: ExerciseDefinition } = {}
      const exerciseNames: string[] = []

      for (const exercise of exercisesData) {
        exerciseNames.push(exercise.name)
        const ex = new ExerciseDefinition(
          exercise.name,
          exercise.keyposes.map((kp: any) => new Pose(kp)),
          exercise.repStyle,
          exercise.weights,
        )
        exercises[exercise.name] = ex
      }

      setAvailableExercises(exerciseNames)
      setExerciseData(exercises)

      // Auto-select first exercise on initial load
      if (isInitialLoadRef.current && exerciseNames.length > 0) {
        const defaultExercise = exerciseNames.find(name =>
          name.includes('Abdominals Stretch Variation Two'),
        ) || exerciseNames[0]
        setSelectedExerciseName(defaultExercise)
        isInitialLoadRef.current = false
      }

    } catch (err) {
    } finally {
      setIsLoadingExercises(false)
    }
  }

  const getCurrentExercise = useCallback((): ExerciseDefinition | null => {
    if (!selectedExerciseName || !exerciseData[selectedExerciseName]) {
      return null
    }

    return exerciseData[selectedExerciseName]
  }, [selectedExerciseName, exerciseData])

  // Load exercises on component mount
  useEffect(() => {
    loadExercises()
  }, [])

  const contextValue = useMemo(() => ({
    availableExercises,
    selectedExerciseName,
    shortExerciseName,
    isLoadingExercises,
    setSelectedExerciseName,
    getCurrentExercise,
  }), [
    availableExercises,
    selectedExerciseName,
    shortExerciseName,
    isLoadingExercises,
    setSelectedExerciseName,
    getCurrentExercise,
  ])

  return (
    <ExerciseContext.Provider value={contextValue}>
      {children}
    </ExerciseContext.Provider>
  )
}

export const useExerciseContext = (): ExerciseContextType => {
  const context = useContext(ExerciseContext)
  if (context === undefined) {
    throw new Error('useExerciseContext must be used within an ExerciseProvider')
  }

  return context
}
